import { useEffect, useState } from 'react';

import { Button, Heading, Modal, Paragraph } from '@zillow/constellation-10';

import {
  type RentalManagerAuthCheckUserInfoDto,
  RentalManagerUserInfoV2WebApiHandler_get,
  type UserInfoRestrictionsDto,
} from '@zg-rentals/ts-rental-manager-api-client';
import { PartialMessage } from '@zg-rentals/backend-styled-components';

import {
  emitClickContactSupportEvent,
  emitClickReturnToZillowEvent,
  emitViewBlockedUserModalEvent,
} from './clickstream/ClickstreamEvents';

import type { BLOCKED_USER_MODAL_TRIGGER_OBJECTS } from './clickstream/ClickstreamEvents';

type Props = {
  restrictions: UserInfoRestrictionsDto;
};

export const BlockedUserModal = ({ restrictions: { partialMessages, title, ctaUrl, ctaText } }: Props) => {
  const [userData, setUserData] = useState<RentalManagerAuthCheckUserInfoDto | null>(null);

  useEffect(() => {
    const fetchUser = async () => {
      const data = await RentalManagerUserInfoV2WebApiHandler_get({});
      setUserData(data);
    };
    fetchUser();
  }, []);

  useEffect(() => {
    if (userData) {
      emitViewBlockedUserModalEvent({ user: userData });
    }
  }, [userData]);

  const handleClose = () => {
    if (userData) {
      emitClickReturnToZillowEvent({ user: userData });
    }
  };

  const handleUrlClick = (e: React.MouseEvent<HTMLAnchorElement>, url: string, text: string) => {
    if (userData) {
      const triggerObject = text
        .toLowerCase()
        .replace(
          /\s+/g,
          '_',
        ) as (typeof BLOCKED_USER_MODAL_TRIGGER_OBJECTS)[keyof typeof BLOCKED_USER_MODAL_TRIGGER_OBJECTS];
      emitClickContactSupportEvent({ user: userData, triggerObject });
    }
  };

  return (
    <Modal
      header={<Heading level={1}>Alert</Heading>}
      body={
        <>
          <Heading level={2} textStyle="heading-sm" css={{ marginBottom: '400' }}>
            {title}
          </Heading>

          <Paragraph>
            <PartialMessage partialMessages={partialMessages} onUrlClick={handleUrlClick} />
          </Paragraph>
        </>
      }
      footer={
        <Button tone="brand" emphasis="filled" onClick={handleClose} asChild>
          <a href={ctaUrl}>{ctaText}</a>
        </Button>
      }
      open={true}
      useDismissProps={{ escapeKey: false, outsidePress: false }}
      closeButton={null}
      size={{ base: 'fullScreen', md: 'md' }}
    />
  );
};

export default BlockedUserModal;
